import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import waytora_logo from "@/assets/logos/internal/waytora_blue_text_logo.svg";
import api from "@/config/axiosConfig";
import { Lock, XCircle, ArrowRight } from "lucide-react";

const Protect: React.FC = () => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      const { data } = await api.auth.post("/password-check", { password });

      if (data.allowed) {
        sessionStorage.setItem("app-access", "true");
        navigate("/");
      }
    } catch (error: any) {
      setError(error.response?.data?.message || "Invalid password");
      setPassword("");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white to-blue-50 relative overflow-hidden">
      {/* Abstract Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-violet-300 rounded-full filter blur-3xl opacity-60"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-300 rounded-full filter blur-3xl opacity-60"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-300 rounded-full filter blur-3xl opacity-60"></div>
      </div>

      <div className="w-full max-w-md p-8 relative">
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-indigo-100">
          <div className="flex flex-col items-center mb-8">
            <img src={waytora_logo} alt="Waytora Logo" className="w-48 mb-6" />
            <div className="flex items-center space-x-3 mb-4">
              <span className="px-3 py-1 bg-indigo-100 text-indigo-600 rounded-full text-sm font-medium tracking-wider">
                PROTECTED
              </span>
            </div>
            <p className="text-indigo-900/70 font-light text-center">
              Please enter the password to access the application.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                <Lock className="text-indigo-400" size={20} strokeWidth={1.5} />
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full pl-14 pr-6 py-4 bg-white border border-indigo-100 rounded-2xl font-light text-indigo-900 placeholder:text-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 transition-all"
                required
                disabled={isLoading}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 border border-red-200 rounded-xl p-4 flex items-center space-x-3">
                <XCircle size={20} strokeWidth={1.5} />
                <p className="font-light">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white py-4 rounded-2xl hover:from-blue-500 hover:via-indigo-500 hover:to-violet-500 disabled:opacity-50 transition-all duration-300 flex items-center justify-center space-x-3 group relative overflow-hidden shadow-lg shadow-indigo-200"
            >
              <span className="font-medium tracking-wide text-lg relative z-10">
                {isLoading ? "CHECKING..." : "UNLOCK ACCESS"}
              </span>
              {!isLoading && (
                <ArrowRight
                  size={20}
                  strokeWidth={1.5}
                  className="opacity-70 group-hover:translate-x-1 transition-transform relative z-10"
                />
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Protect;
